import api from './api';
import type { PmsDatabaseType } from '../types/pms';
import type { PatientStatus } from './patientStatusService';
import type {
  CareProviderRole,
  PatientLinkType,
  RefillType,
  SpecialtyDrugStage,
} from '../patient/patientProfileCatalogue';
import type { PhoneType } from '../utils/patientMessagingNumbers';

/**
 * PRX-80 — the one place these routes are named (Task 6).
 *
 * The backend endpoints this service calls are fixed by two other briefs in
 * the same plan (Task 1: the record read; Task 2: the write gate and the
 * care-provider / patient-link / specialty-drug writes) and mirrored here
 * verbatim — this file is the only frontend copy of these types, so a shape
 * change on either side shows up as one diff, not a hunt across callers.
 *
 * Every route carries the pharmacy id in the path, and every write requires
 * `editPatients` behind `assertWritable` on the server — see
 * `Patient360ReadOnlyContext` for the frontend half of that gate.
 */

export type PatientRecordPresence = 'phox' | 'staged_only';
export type PatientReadOnlyReason = 'inactive' | 'deceased' | 'transferred' | 'not_in_phox';

/** The 14 PHOX-owned fields (W1 Task 4's shape). Every value is present — a
 * missing field is `null`/`false`, never an absent key, so a spread-merge in
 * a form never silently drops one. */
export interface PatientProfileFields {
  salutation: string | null;
  alsoKnownAs: string | null;
  profession: string | null;
  refillType: RefillType | null;
  refillNotes: string | null;
  blisterNotes: string | null;
  /** YYYY-MM-DD, or null. */
  blisterAuthDueDate: string | null;
  /** YYYY-MM-DD, or null. */
  medscheckReminderDate: string | null;
  medscheckBooked: boolean;
  medscheckOptOut: boolean;
  polypharmacy: boolean;
  isVip: boolean;
  referredBy: string | null;
  deliveryNotes: string | null;
}

/** The PATCH body — every field optional, since a save only ever carries what changed. */
export type UpdateProfileFieldsBody = Partial<PatientProfileFields>;

export interface PatientCareClinicSummary {
  id: string;
  name: string;
  phone: string | null;
  fax: string | null;
  city: string | null;
}

export interface PatientCareProviderDto {
  id: string;
  role: CareProviderRole;
  providerName: string | null;
  providerCpso: string | null;
  pmsDoctorId: string | null;
  careClinicId: string | null;
  careClinic: PatientCareClinicSummary | null;
}

export interface UpsertCareProviderBody {
  role: CareProviderRole;
  providerName?: string;
  providerCpso?: string;
  pmsDoctorId?: string;
  careClinicId?: string;
}

export interface PatientLinkDto {
  id: string;
  linkType: PatientLinkType;
  linkedPatientId: string | null;
  /** Resolved from the linked patient's own row when one exists; never guessed. */
  linkedPatientName: string | null;
  /** PRX-34 — the connected patient's composite id, to open their file. Same-pharmacy only. */
  linkedPatientCompositeId: string | null;
  relationship: string | null;
  contactName: string | null;
  contactPhone: string | null;
  notes: string | null;
}

export interface UpsertPatientLinkBody {
  linkType: PatientLinkType;
  linkedPatientId?: string;
  relationship?: string;
  contactName?: string;
  contactPhone?: string;
  notes?: string;
}

export interface PatientSpecialtyDrugDto {
  id: string;
  drugName: string;
  stage: SpecialtyDrugStage | null;
  receivedDate: string | null;
  renewalDate: string | null;
  requiresRenewal: boolean;
  notes: string | null;
  prescriberCareProviderId: string | null;
}

export interface UpsertSpecialtyDrugBody {
  drugName: string;
  stage?: SpecialtyDrugStage;
  receivedDate?: string;
  renewalDate?: string;
  requiresRenewal?: boolean;
  notes?: string;
  prescriberCareProviderId?: string;
}

/**
 * One staged Zoho phone number the patient does not have on file. Mirrors
 * `StagedContactPhoneDto` (`backend-v2/src/patient/dto/patient-record.dto.ts`)
 * exactly; `patientRecordService.stagedContact.drift.spec.ts` pins both shapes
 * against each other.
 *
 * `value` is the ten digits the backend's `normalizePhone` parsed — a cell that
 * does not parse is omitted there, so nothing here is raw export text. `kind`
 * is the PHOX line type the Zoho column suggests, so an editor can pre-select
 * it; it is a suggestion off a spreadsheet, not a verified attribute of the
 * number, and nothing about SMS is implied. The import marks no number textable
 * and picks no SMS recipient (`docs/workflows/patient-sms-number.md`).
 */
export interface StagedContactPhone {
  value: string;
  kind: PhoneType;
}

/**
 * What the staged Zoho record says this patient's contacts are, minus
 * everything PHOX already holds — the ONE sanctioned projection of the staged
 * payload's deferred block (ADR-073). Mirrors `StagedContactDto`.
 *
 * Nothing here has been written anywhere. Both lists are what a human could
 * choose to add; empty means the staged row offers nothing PHOX does not
 * already have. Never an address, never the health card.
 */
export interface StagedContact {
  phones: StagedContactPhone[];
  /** Null when Zoho has none, it is unreadable, or PHOX already holds it. */
  email: string | null;
}

/** GET `.../record` as the API serves it — one composite read covering a PHOX
 * row and, when there is none, a staged-only Zoho profile. */
export interface PatientRecordResponse {
  presence: PatientRecordPresence;
  pharmacyId: string;
  /** The bare PMS patient id, e.g. Fillware Patient.PatientID as text. Null for
   * a PHOX patient with no patient number — the DTO's own type
   * (`PatientRecordResponseDto.pmsPatientId`), pinned by the drift spec. */
  pmsPatientId: string | null;
  /** `<pharmacyId>_<pmsPatientId>`; null when presence is 'staged_only'. */
  compositeId: string | null;
  /** The PHOX patients.id uuid; null when presence is 'staged_only'. */
  patientId: string | null;
  databaseType: PmsDatabaseType;
  /** R5 — the ONE flag. True for an inactivated/deceased/transferred patient AND for staged_only. */
  readOnly: boolean;
  readOnlyReason: PatientReadOnlyReason | null;
  status: { status: PatientStatus; reason: string | null; note: string | null; changedAt: string | null } | null;
  profileFields: PatientProfileFields;
  careProviders: PatientCareProviderDto[];
  links: PatientLinkDto[];
  specialtyDrugs: PatientSpecialtyDrugDto[];
  permanentNote: { id: string; content: string; updatedAt: string | null; updatedBy: string | null } | null;
  /**
   * The staged Zoho phone numbers and e-mail this patient does not have on
   * file, for staff to add deliberately (the import writes no contact data —
   * ADR-073). NULL when there is no staged row at all, which is every patient
   * at every GreenShield pharmacy.
   */
  stagedContact: StagedContact | null;
  zoho: { staged: boolean; appliedAt: string | null };
}

/**
 * PRX-80 — the composite record read, the PHOX-owned profile-fields PATCH,
 * and the care-provider / patient-link / specialty-drug writes. Nothing here
 * falls back to a default when a call fails: an error propagates to TanStack
 * Query, and the caller renders nothing rather than a plausible wrong record.
 */
const patientRecordService = {
  async getPatientRecord(pharmacyId: string, patientId: string): Promise<PatientRecordResponse> {
    const response = await api.get<PatientRecordResponse>(
      `/api/patients/${pharmacyId}/${patientId}/record`,
    );
    return response.data;
  },

  /**
   * PRX-80 Task 9 — a pharmacy's own active care-clinic directory
   * (`PrescriberAndClinicCard`'s clinic select). TWO path segments, not
   * three: a care clinic is pharmacy reference data, not a patient relation.
   */
  async getCareClinics(pharmacyId: string): Promise<PatientCareClinicSummary[]> {
    const response = await api.get<PatientCareClinicSummary[]>(
      `/api/patients/${pharmacyId}/care-clinics`,
    );
    return response.data;
  },

  async updateProfileFields(
    pharmacyId: string,
    patientId: string,
    body: UpdateProfileFieldsBody,
  ): Promise<PatientProfileFields> {
    const response = await api.patch<PatientProfileFields>(
      `/api/patients/${pharmacyId}/${patientId}/profile-fields`,
      body,
    );
    return response.data;
  },

  async upsertCareProvider(
    pharmacyId: string,
    patientId: string,
    body: UpsertCareProviderBody,
  ): Promise<PatientCareProviderDto> {
    const response = await api.post<PatientCareProviderDto>(
      `/api/patients/${pharmacyId}/${patientId}/care-providers`,
      body,
    );
    return response.data;
  },

  async updateCareProvider(
    pharmacyId: string,
    patientId: string,
    careProviderId: string,
    body: UpsertCareProviderBody,
  ): Promise<PatientCareProviderDto> {
    const response = await api.patch<PatientCareProviderDto>(
      `/api/patients/${pharmacyId}/${patientId}/care-providers/${careProviderId}`,
      body,
    );
    return response.data;
  },

  async deleteCareProvider(pharmacyId: string, patientId: string, careProviderId: string): Promise<void> {
    await api.delete(`/api/patients/${pharmacyId}/${patientId}/care-providers/${careProviderId}`);
  },

  async createPatientLink(
    pharmacyId: string,
    patientId: string,
    body: UpsertPatientLinkBody,
  ): Promise<PatientLinkDto> {
    const response = await api.post<PatientLinkDto>(
      `/api/patients/${pharmacyId}/${patientId}/links`,
      body,
    );
    return response.data;
  },

  async updatePatientLink(
    pharmacyId: string,
    patientId: string,
    linkId: string,
    body: UpsertPatientLinkBody,
  ): Promise<PatientLinkDto> {
    const response = await api.patch<PatientLinkDto>(
      `/api/patients/${pharmacyId}/${patientId}/links/${linkId}`,
      body,
    );
    return response.data;
  },

  async deletePatientLink(pharmacyId: string, patientId: string, linkId: string): Promise<void> {
    await api.delete(`/api/patients/${pharmacyId}/${patientId}/links/${linkId}`);
  },

  async createSpecialtyDrug(
    pharmacyId: string,
    patientId: string,
    body: UpsertSpecialtyDrugBody,
  ): Promise<PatientSpecialtyDrugDto> {
    const response = await api.post<PatientSpecialtyDrugDto>(
      `/api/patients/${pharmacyId}/${patientId}/specialty-drugs`,
      body,
    );
    return response.data;
  },

  async updateSpecialtyDrug(
    pharmacyId: string,
    patientId: string,
    specialtyDrugId: string,
    body: UpsertSpecialtyDrugBody,
  ): Promise<PatientSpecialtyDrugDto> {
    const response = await api.patch<PatientSpecialtyDrugDto>(
      `/api/patients/${pharmacyId}/${patientId}/specialty-drugs/${specialtyDrugId}`,
      body,
    );
    return response.data;
  },
};

export default patientRecordService;
